import { useEffect, useState } from "react";
import User from "../models/User";
import useFetch from "../../hooks/use-fetch";
import { toast } from "react-toastify";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel } from "@mui/material";
import { formatDateTime } from "../Pages/Reports/Reports";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

type Order = "asc" | "desc";

interface UserConsumption {
    id: number
    user: User
    report_id: string | null
    report_title: string
    lines: number
    created_at: string
}


const UserConsumptions = () => {
    const { get } = useFetch()
    const { userId } = useParams()

    const [consumptions, setConsumptions] = useState<UserConsumption[]>([])
    const [filteredConsumptions, setFilteredConsumptions] = useState<UserConsumption[]>([])

    const [groupedUser, setGroupedUser] = useState<any[]>([])

    const [orderBy, setOrderBy] = useState<keyof UserConsumption>("created_at")
    const [order, setOrder] = useState<Order>("asc")
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(5)
    const [searchTerm, setSearchTerm] = useState<string>("")

    const [startDate, setStartDate] = useState<Date | null>(null)
    const [endDate, setEndDate] = useState<Date | null>(null)

    useEffect(() => {
        fetchConsumptions()
    }, [])

    useEffect(() => {
        setPage(0);
    }, [searchTerm])

    const fetchConsumptions = async () => {
        let url = '/users/consumptions/'
        if (userId){
            url += `?user_id=${userId}`
        }
        try {
            const data = await get(url)
            setConsumptions(data.consumptions)
            setFilteredConsumptions(data.consumptions)
            groupUser(data.consumptions)
        } catch (err) {
            toast.error('Error fetching consumptions')
        }
    }

    useEffect(() => {
        const noSearch = !searchTerm || searchTerm.trim() === ""
        let updated = consumptions
        
        if (!noSearch) {
            let lower = searchTerm.toLowerCase()
            updated = consumptions.filter((c) => {
                const matchesSearch =
                    noSearch ||
                    (c.user && c.user.email && c.user.email.toLowerCase().includes(lower)) ||
                    c.report_title.toLowerCase().includes(lower)
                return matchesSearch
            });
        }

        if (startDate){
            updated = updated.filter((c) => {
                return new Date(c.created_at) >= startDate
            })
        }
        if (endDate){
            updated = updated.filter((c) => {
                return new Date(c.created_at) <= endDate
            })
        }

        setFilteredConsumptions(updated);
        groupUser(updated)
    }, [consumptions, searchTerm, startDate, endDate])

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSort = (property: keyof UserConsumption) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);

        const sorted = [...filteredConsumptions].sort((a, b) => {
            if (a[property]! < b[property]!) return isAsc ? -1 : 1;
            if (a[property]! > b[property]!) return isAsc ? 1 : -1;
            return 0;
        });
        setFilteredConsumptions(sorted);
    };

    const handleViewReport = (reportId: string) => {
        console.log(reportId)
    }
    

    const groupUser = (tmpConsumptions: UserConsumption[]) => {
        const sums: Record<string, number> = {}
        tmpConsumptions.forEach(c => {
            const email = c?.user?.email ?? c?.user?.id
            if (!email) return

            const linesNum = typeof c.lines === 'number' ? c.lines : Number(c.lines) || 0
            sums[email] = (sums[email] || 0) + linesNum
        })

        const seen: Record<string, boolean> = {}
        const result = tmpConsumptions.map(c => {
            const email = c?.user?.email ?? c?.user?.id
            if (!email) return ''
            if (!seen[email]) {
                seen[email] = true
                return sums[email]
            }
            return ''
        })

        setGroupedUser(result)
        return result
    }

    return (
        <div className="main-container">
            <div className="content-header"></div>
            
            <div style={{margin: '25px 0', width: '100%'}}>
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Cerca consumi"
                    className="search-input"
                />
            </div>

            <div style={{display: 'flex', width: '100%', justifyContent:'flex-start', gap: '5rem', marginBottom: '2rem'}}>
                <div className='col col-sm'>
                    <label style={{fontWeight: 'bold'}}>Data inizio</label>
                    <DatePicker
                        value={startDate ? dayjs(startDate) : null}
                        onChange={(newValue) => {
                            setStartDate(newValue ? newValue.toDate() : null);
                        }}
                        format="DD-MM-YYYY"
                        slotProps={{
                        textField: {
                            required: true,
                            fullWidth: true,
                        },
                        }}
                    />
                </div>
                <div className='col col-sm'>
                    <label  style={{fontWeight: 'bold'}}>Data fine</label>
                    <DatePicker
                        value={endDate ? dayjs(endDate) : null}
                        onChange={(newValue) => {
                            setEndDate(newValue ? newValue.toDate() : null);
                        }}
                        format="DD-MM-YYYY"
                        slotProps={{
                        textField: {
                            required: true,
                            fullWidth: true,
                        },
                        }}
                    />
                </div>
            </div>
            <TableContainer component={Paper} sx={{ flex: 1, width: '1000pt', height: '100%', borderRadius:'16px'}}>
                <Table>
                    <TableHead>
                        <TableRow>
                        {[
                            { id: "id", label: "ID" },
                            { id: "email", label: "Utente" },
                            { id: "report_title", label: "Report" },
                            { id: "lines", label: "Linee" },
                            { id: "created_at", label: "Data" },
                            { id: "total", label: "Totale periodo" },
                            { id: "action", label: ''},
                        ].map((col) => (
                            <TableCell key={col.id} sx={{fontSize: '14px', fontWeight: '600'}}>
                                <TableSortLabel
                                    active={orderBy === col.id}
                                    direction={orderBy === col.id ? order : "asc"}
                                    onClick={() => handleSort(col.id as keyof UserConsumption)}
                                >
                                    {col.label}
                                </TableSortLabel>
                            </TableCell>
                        ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredConsumptions
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((consumption, idx) => (
                            <TableRow key={consumption.id}>
                            <TableCell>{consumption.id}</TableCell>
                            <TableCell>{consumption.user.email}</TableCell>
                            <TableCell>{consumption.report_title}</TableCell>
                            <TableCell>{consumption.lines}</TableCell>
                            <TableCell>{consumption.created_at ? formatDateTime(consumption.created_at) : ''}</TableCell>
                            <TableCell>{groupedUser[idx] || ''}</TableCell>
                            {/* <TableCell>
                                <div className="table-actions">
                                    <FontAwesomeIcon
                                        icon={faEye}
                                        className={`action-icon export ${!consumption.report_id ? "disabled" : ""}`}
                                        onClick={() => {
                                            consumption.report_id && handleViewReport(consumption.report_id)
                                        }}
                                        style={!consumption.report_id ? { pointerEvents: "none", opacity: 0.5 } : {}}
                                    />
                                </div>
                            </TableCell> */}
                            </TableRow>
                        ))}
                    </TableBody>

                </Table>


                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredConsumptions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
        </div>
    )
}


export default UserConsumptions